"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { $convertFromMarkdownString } from "@lexical/markdown";
import { $getRoot, EditorState, COMMAND_PRIORITY_HIGH, KEY_ENTER_COMMAND, KEY_ESCAPE_COMMAND, TextNode, $createParagraphNode, $createTextNode, $isElementNode, type LexicalNode, type RootNode } from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ListNode, ListItemNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { CodeHighlightNode, CodeNode, $isCodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { MentionNode, $createMentionNode } from "@/components/drops/create/lexical/nodes/MentionNode";
import { HashtagNode } from "@/components/drops/create/lexical/nodes/HashtagNode";
import { MENTION_TRANSFORMER } from "@/components/drops/create/lexical/transformers/MentionTransformer";
import { HASHTAG_TRANSFORMER } from "@/components/drops/create/lexical/transformers/HastagTransformer";
import ExampleTheme from "@/components/drops/create/lexical/ExampleTheme";
import NewMentionsPlugin, { NewMentionsPluginHandles } from "@/components/drops/create/lexical/plugins/mentions/MentionsPlugin";
import { MentionedUser } from "@/entities/IDrop";
import { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import CreateDropEmojiPicker from "../CreateDropEmojiPicker";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import EmojiPlugin from "@/components/drops/create/lexical/plugins/emoji/EmojiPlugin";
import { EmojiNode } from "@/components/drops/create/lexical/nodes/EmojiNode";
import { SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
import { normalizeDropMarkdown, exportDropMarkdown } from "./normalizeDropMarkdown";
import { addBlankLinePlaceholders, removeBlankLinePlaceholders } from "./blankLinePlaceholders";
interface EditDropLexicalProps {
  readonly initialContent: string;
  readonly initialMentions: ApiDropMentionedUser[];
  readonly waveId: string | null;
  readonly isSaving: boolean;
  readonly onSave: (content: string, mentions: ApiDropMentionedUser[]) => void;
  readonly onCancel: () => void;
}
const MAX_MENTION_RECONSTRUCTION_PASSES = 20;
const EDIT_MARKDOWN_TRANSFORMERS = [...SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE, MENTION_TRANSFORMER, HASHTAG_TRANSFORMER];
const convertCodeNodesToFences = (root: RootNode) => {
  const stack: LexicalNode[] = [...root.getChildren()];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    if ($isCodeNode(node)) {
      const language = node.getLanguage?.() ?? "";
      const safeLanguage = language.trim().replaceAll(/[`\n\r]/g, "");
      const codeText = node.getTextContent();
      const normalizedCode = codeText.endsWith("\n") ? codeText : `${codeText}\n`;
      const maxExistingFence = (codeText.match(/`+/g) ?? []).reduce((max, match) => Math.max(max, match.length), 0);
      const fenceLength = Math.max(3, maxExistingFence + 1);
      const fence = "`".repeat(fenceLength);
      const openFence = safeLanguage ? `${fence}${safeLanguage}` : fence;
      const fencedMarkdown = `${openFence}\n${normalizedCode}${fence}`;
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(fencedMarkdown));
      node.replace(paragraph);
      continue;
    }
    if ($isElementNode(node)) {
      stack.push(...node.getChildren());
    }
  }
};
function reconstructSplitMention(currentNode: any, nextNode: any, mentionStart: RegExpMatchArray, mentionEnd: RegExpMatchArray) {
  const fullMention = mentionStart[0] + mentionEnd[0];
  const mentionRegex = /@\[(\w+)\]/;
  const mentionMatch = mentionRegex.exec(fullMention);
  if (!mentionMatch) return false;
  const handle = mentionMatch[1];
  const mentionNode = $createMentionNode(`@${handle}`);
  const currentText = currentNode.getTextContent();
  const nextText = nextNode.getTextContent();
  const beforeMention = currentText.substring(0, currentText.length - mentionStart[0].length);
  const afterMention = nextText.substring(mentionEnd[0].length);
  if (beforeMention) {
    currentNode.setTextContent(beforeMention);
    currentNode.insertAfter(mentionNode);
  } else {
    currentNode.remove();
    nextNode.insertBefore(mentionNode);
  }
  if (afterMention) {
    nextNode.setTextContent(afterMention);
  } else {
    nextNode.remove();
  }
  return true;
}
function processSplitMentions(textNodes: Array<TextNode>): boolean {
  for (let i = 0; i < textNodes.length - 1; i++) {
    const currentNode = textNodes[i];
    const nextNode = textNodes[i + 1];
    const currentText = currentNode?.getTextContent();
    const nextText = nextNode?.getTextContent();
    const mentionStart = currentText?.match(/@\[\w*$/);
    const mentionEnd = nextText?.match(/^\w*\]/);
    if (mentionStart && mentionEnd) {
      try {
        if (reconstructSplitMention(currentNode, nextNode, mentionStart, mentionEnd)) {
          return true;
        }
      } catch (error) {
        console.warn("Failed to reconstruct split mention", error);
      }
    }
  }
  return false;
}
function InitialContentPlugin(t0) {
  const $ = _c(4);
  const {
    initialContent
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== editor || $[1] !== initialContent) {
    t1 = () => {
      editor.update(() => {
        const normalizedContent = normalizeDropMarkdown(initialContent);
        $convertFromMarkdownString(normalizedContent, EDIT_MARKDOWN_TRANSFORMERS);
        const root = $getRoot();
        convertCodeNodesToFences(root);
        let needsAnotherPass = true;
        let passCount = 0;
        while (needsAnotherPass && passCount < MAX_MENTION_RECONSTRUCTION_PASSES) {
          const textNodes = root.getAllTextNodes();
          const hasUnprocessedMentions = textNodes.some(_temp);
          if (hasUnprocessedMentions) {
            break;
          }
          needsAnotherPass = processSplitMentions(textNodes);
          passCount = passCount + 1;
        }
        if (needsAnotherPass && passCount >= MAX_MENTION_RECONSTRUCTION_PASSES) {
          console.warn("Mention reconstruction reached max passes without converging");
        }
        root.selectEnd();
      });
    };
    t2 = [editor, initialContent];
    $[0] = editor;
    $[1] = initialContent;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp(node) {
  return /@\[\w+\]/.test(node.getTextContent());
}
function FocusPlugin(t0) {
  const $ = _c(4);
  const {
    isApp
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== editor || $[1] !== isApp) {
    t1 = () => {
      const focusEditor = () => {
        editor.focus();
        requestAnimationFrame(_temp2);
      };
      if (isApp) {
        const timeouts = [setTimeout(focusEditor, 100), setTimeout(focusEditor, 350), setTimeout(focusEditor, 600)];
        return () => timeouts.forEach(clearTimeout);
      } else {
        focusEditor();
      }
    };
    t2 = [editor, isApp];
    $[0] = editor;
    $[1] = isApp;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {
  const contentEditable = document.querySelector("[contenteditable=\"true\"]") as HTMLElement;
  if (contentEditable && document.activeElement !== contentEditable) {
    contentEditable.focus();
    contentEditable.click();
  }
}
function KeyboardPlugin(t0) {
  const $ = _c(17);
  const {
    onSave,
    onCancel,
    isSaving,
    initialContent,
    mentionsRef
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  if ($[0] !== initialContent) {
    t1 = removeBlankLinePlaceholders(initialContent);
    $[0] = initialContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sanitizedInitialContent = t1;
  let t2;
  if ($[2] !== editor || $[3] !== isSaving || $[4] !== mentionsRef || $[5] !== onCancel || $[6] !== onSave || $[7] !== sanitizedInitialContent) {
    t2 = () => {
      const removeEscapeListener = editor.registerCommand(KEY_ESCAPE_COMMAND, () => {
        onCancel();
        return true;
      }, COMMAND_PRIORITY_HIGH);
      const removeEnterListener = editor.registerCommand(KEY_ENTER_COMMAND, event => {
        if (mentionsRef.current?.isMentionsOpen()) {
          return false;
        }
        if (event?.shiftKey) {
          return false;
        }
        if (!isSaving) {
          const currentMarkdown = exportDropMarkdown(editor.getEditorState(), EDIT_MARKDOWN_TRANSFORMERS);
          const sanitizedCurrentMarkdown = removeBlankLinePlaceholders(currentMarkdown);
          if (sanitizedCurrentMarkdown.trim() === sanitizedInitialContent.trim()) {
            onCancel();
          } else {
            onSave();
          }
        }
        return true;
      }, COMMAND_PRIORITY_HIGH);
      return () => {
        removeEscapeListener();
        removeEnterListener();
      };
    };
    $[2] = editor;
    $[3] = isSaving;
    $[4] = mentionsRef;
    $[5] = onCancel;
    $[6] = onSave;
    $[7] = sanitizedInitialContent;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== editor || $[10] !== initialContent || $[11] !== isSaving || $[12] !== mentionsRef || $[13] !== onCancel || $[14] !== onSave || $[15] !== sanitizedInitialContent) {
    t3 = [editor, onSave, onCancel, isSaving, initialContent, mentionsRef, sanitizedInitialContent];
    $[9] = editor;
    $[10] = initialContent;
    $[11] = isSaving;
    $[12] = mentionsRef;
    $[13] = onCancel;
    $[14] = onSave;
    $[15] = sanitizedInitialContent;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  useEffect(t2, t3);
  return null;
}
const EditDropLexical: React.FC<EditDropLexicalProps> = t0 => {
  const $ = _c(50);
  const {
    initialContent,
    initialMentions,
    waveId,
    isSaving,
    onSave,
    onCancel
  } = t0;
  const [editorState, setEditorState] = useState(null);
  const [mentionedUsers, setMentionedUsers] = useState(initialMentions);
  const editorRef = useRef(null);
  const mentionsRef = useRef(null);
  const {
    isApp
  } = useDeviceInfo();
  let t1;
  if ($[0] !== initialContent) {
    t1 = normalizeDropMarkdown(initialContent);
    $[0] = initialContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalizedInitialContent = t1;
  let t2;
  if ($[2] !== normalizedInitialContent) {
    t2 = addBlankLinePlaceholders(normalizedInitialContent);
    $[2] = normalizedInitialContent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const editorInitialContent = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      namespace: "EditDropLexical",
      theme: ExampleTheme,
      onError: _temp3,
      nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, HorizontalRuleNode, AutoLinkNode, LinkNode, MentionNode, HashtagNode, EmojiNode]
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const initialConfig = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = editorState_0 => {
      setEditorState(editorState_0);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleEditorChange = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = user => {
      const newMention = {
        mentioned_profile_id: user.mentioned_profile_id,
        handle_in_content: user.handle_in_content
      };
      setMentionedUsers(prev => {
        if (prev.some(m => m.mentioned_profile_id === newMention.mentioned_profile_id)) {
          return prev;
        }
        return [...prev, newMention];
      });
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleMentionSelect = t5;
  let t6;
  if ($[7] !== editorState || $[8] !== mentionedUsers || $[9] !== normalizedInitialContent || $[10] !== onCancel || $[11] !== onSave) {
    t6 = () => {
      if (!editorState) {
        return;
      }
      const markdown = exportDropMarkdown(editorState, EDIT_MARKDOWN_TRANSFORMERS);
      const sanitizedMarkdown = removeBlankLinePlaceholders(markdown);
      if (sanitizedMarkdown.trim() === normalizedInitialContent.trim()) {
        onCancel();
        return;
      }
      onSave(sanitizedMarkdown, mentionedUsers);
    };
    $[7] = editorState;
    $[8] = mentionedUsers;
    $[9] = normalizedInitialContent;
    $[10] = onCancel;
    $[11] = onSave;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleSave = t6;
  let t10;
  let t11;
  let t12;
  let t13;
  let t7;
  let t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <RichTextPlugin contentEditable={<div className="tw-relative"><ContentEditable className="tw-min-h-[40px] tw-w-full tw-resize-none tw-overflow-y-auto tw-overflow-x-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-py-2.5 tw-pl-3 tw-pr-10 tw-text-sm tw-text-iron-100 tw-outline-none tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 focus:tw-border-primary-400 desktop-hover:hover:tw-scrollbar-thumb-iron-300" style={{
        fontFamily: "inherit",
        lineHeight: "1.4"
      }} /><CreateDropEmojiPicker top="tw-top-1" /></div>} placeholder={<div className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-2 tw-text-sm tw-text-iron-500">Edit message...</div>} ErrorBoundary={LexicalErrorBoundary} />;
    t8 = <OnChangePlugin onChange={handleEditorChange} />;
    t9 = <HistoryPlugin />;
    t10 = <PlainTextPastePlugin />;
    t11 = <MarkdownShortcutPlugin transformers={SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE} />;
    t12 = <ListPlugin />;
    t13 = <LinkPlugin />;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t10 = $[13];
    t11 = $[14];
    t12 = $[15];
    t13 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t14;
  if ($[20] !== waveId) {
    t14 = <NewMentionsPlugin ref={mentionsRef} waveId={waveId} onSelect={handleMentionSelect} />;
    $[20] = waveId;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <EmojiPlugin />;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== editorInitialContent) {
    t16 = <InitialContentPlugin initialContent={editorInitialContent} />;
    $[23] = editorInitialContent;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== isApp) {
    t17 = <FocusPlugin isApp={isApp} />;
    $[25] = isApp;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== handleSave || $[28] !== isSaving || $[29] !== normalizedInitialContent || $[30] !== onCancel) {
    t18 = <KeyboardPlugin onSave={handleSave} onCancel={onCancel} isSaving={isSaving} initialContent={normalizedInitialContent} mentionsRef={mentionsRef} />;
    $[27] = handleSave;
    $[28] = isSaving;
    $[29] = normalizedInitialContent;
    $[30] = onCancel;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t14 || $[33] !== t16 || $[34] !== t17 || $[35] !== t18) {
    t19 = <LexicalComposer initialConfig={initialConfig}><div ref={editorRef} className="tw-relative">{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}</div></LexicalComposer>;
    $[32] = t14;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== handleSave || $[38] !== isApp || $[39] !== onCancel) {
    t20 = !isApp && <div className="tw-mt-1 tw-flex tw-items-center tw-text-xs tw-text-iron-400">escape to{" "}<button onClick={onCancel} className="tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-[3px] tw-font-medium tw-text-primary-400 tw-transition focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 desktop-hover:hover:tw-underline">cancel</button>{" "}• enter to{" "}<button onClick={handleSave} className="tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-[3px] tw-font-medium tw-text-primary-400 tw-transition focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 desktop-hover:hover:tw-underline">save</button></div>;
    $[37] = handleSave;
    $[38] = isApp;
    $[39] = onCancel;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== handleSave || $[42] !== isApp || $[43] !== isSaving || $[44] !== onCancel) {
    t21 = isApp && <div className="tw-mb-2 tw-mt-3"><div className="tw-flex tw-gap-x-2"><button onClick={onCancel} disabled={isSaving} className="tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-iron-300 tw-transition-colors tw-duration-150 active:tw-bg-iron-700 disabled:tw-opacity-50">Cancel</button><button onClick={handleSave} disabled={isSaving} className="tw-rounded-lg tw-border-0 tw-bg-primary-500 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-transition-colors tw-duration-150 active:tw-bg-primary-600 disabled:tw-opacity-50">{isSaving ? "Saving..." : "Save"}</button></div></div>;
    $[41] = handleSave;
    $[42] = isApp;
    $[43] = isSaving;
    $[44] = onCancel;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t19 || $[47] !== t20 || $[48] !== t21) {
    t22 = <div className="tw-w-full" data-editor-mode="true">{t19}{t20}{t21}</div>;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  return t22;
};
export default EditDropLexical;
function _temp3(error) {
  console.error(error);
}